(() => {
  'use strict';

  if (window.__momentierAnalytics?.version) return;

  const context = window.MomentierAnalyticsContext || {};
  const state = window.__momentierAnalytics = {
    version: '1.0.0',
    events: [],
    vitals: { cls: 0, lcp: 0, inp: 0 }
  };
  document.documentElement.dataset.momentierAnalytics = state.version;

  const nowId = () => {
    try { return crypto.randomUUID(); }
    catch (_) { return `${Date.now()}-${Math.random().toString(16).slice(2)}`; }
  };
  const number = value => {
    const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const text = value => String(value || '').trim().replace(/\s+/g, ' ');
  const page = {
    page_type: context.page_type || '',
    page_path: location.pathname,
    page_title: document.title,
    currency: context.currency || 'INR'
  };

  function publishShopify(name, payload, attempt = 0) {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish === 'function') {
      try { Promise.resolve(publish(`momentier:${name.replace(/^momentier_/, '')}`, payload)).catch(() => {}); }
      catch (_) {}
      return;
    }
    if (attempt < 20) setTimeout(() => publishShopify(name, payload, attempt + 1), 500);
  }

  function track(name, payload = {}) {
    const event = {
      event_id: nowId(),
      event_time: new Date().toISOString(),
      ...page,
      ...payload
    };
    state.events.push({ name, ...event });
    if (state.events.length > 50) state.events.shift();
    document.documentElement.dataset.momentierLastEvent = name;
    const marker = name.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    document.documentElement.dataset[marker] = event.event_id;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, momentier: event });
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', name, event); } catch (_) {}
    }
    publishShopify(name, event);
    document.dispatchEvent(new CustomEvent('momentier:analytics', { detail: { name, payload: event } }));
  }

  function currentProduct(variantId) {
    const product = context.product;
    if (product) {
      const variant = (product.variants || []).find(item => String(item.id) === String(variantId)) || {};
      return {
        item_id: String(product.id || ''),
        item_name: product.title || '',
        item_handle: product.handle || '',
        item_variant_id: String(variant.id || variantId || product.variant_id || ''),
        item_variant: variant.title || product.variant_title || '',
        price: number(variant.price ?? product.price)
      };
    }

    const button = document.querySelector(`.mlx-card__add[data-variant-id="${CSS.escape(String(variantId || ''))}"]`);
    if (!button) return { item_variant_id: String(variantId || '') };
    const card = button.closest('.mlx-card');
    const activeSize = card?.querySelector('.mlx-card__size.is-active');
    return {
      item_id: String(button.dataset.productId || ''),
      item_name: button.dataset.productTitle || text(card?.querySelector('.mlx-card__name')?.textContent),
      item_handle: button.dataset.productHandle || '',
      item_variant_id: String(variantId || button.dataset.variantId || ''),
      item_variant: activeSize?.textContent?.trim() || button.dataset.variantTitle || '',
      price: number(activeSize?.dataset.priceCents ? Number(activeSize.dataset.priceCents) / 100 : button.dataset.priceCents ? Number(button.dataset.priceCents) / 100 : card?.querySelector('.mlx-card__p')?.textContent)
    };
  }

  function cartCount() {
    const badge = document.querySelector('.mom-rhead__cart [data-cart-count], .mom-rhead__cart .cart-count, [data-cart-count]');
    return number(badge?.textContent || context.cart?.count || 0);
  }

  if (context.product) {
    const item = currentProduct(context.product.variant_id);
    track('momentier_view_item', {
      value: item.price,
      items: [{ ...item, quantity: 1 }]
    });
  }

  let recentAddKey = '';
  let recentAddAt = 0;
  function trackAdded(detail = {}) {
    const item = currentProduct(detail.variantId);
    const quantity = Math.max(1, number(detail.quantity || document.querySelector('.mep-quantity input, .mom-rdk__qty input')?.value || 1));
    const addKey = `${item.item_variant_id}:${quantity}`;
    if (addKey === recentAddKey && Date.now() - recentAddAt < 3000) return;
    recentAddKey = addKey;
    recentAddAt = Date.now();
    track('momentier_add_to_cart', {
      source: detail.source || (context.product?.handle === 'discovery-set' ? 'discovery_set' : context.product ? 'product_page' : 'product_card'),
      value: Number((item.price * quantity).toFixed(2)),
      items: [{ ...item, quantity }]
    });
  }

  document.addEventListener('cart:item-added', event => {
    trackAdded(event.detail || {});
  });

  // Fastrr can own the click before the theme's quick-add callback runs. Observe the
  // confirmed Shopify response and publish analytics from its returned line item.
  // The original response is returned immediately; cloning is asynchronous and does
  // not alter Fastrr's request, response body, drawer, or checkout sequence.
  const storefrontFetch = window.fetch;
  if (typeof storefrontFetch === 'function') {
    window.fetch = function(input, init) {
      const url = typeof input === 'string' ? input : input?.url || '';
      const responsePromise = storefrontFetch.apply(this, arguments);
      if (/\/cart\/add(?:\.js)?(?:\?|$)/.test(url)) {
        responsePromise.then(response => {
          if (!response.ok) return;
          response.clone().json().then(data => {
            const added = data?.items?.[0] || data;
            trackAdded({
              variantId: added?.variant_id || added?.id,
              quantity: added?.quantity || 1,
              source: 'network_confirmed'
            });
          }).catch(() => {});
        }).catch(() => {});
      }
      return responsePromise;
    };
  }

  const drawerIsOpen = () => document.body.classList.contains('cart-sidebar-show') || document.body.classList.contains('srsc-overflow-hidden');
  let drawerOpen = drawerIsOpen();
  new MutationObserver(() => {
    const open = drawerIsOpen();
    if (open && !drawerOpen) {
      track('momentier_cart_viewed', {
        source: document.body.classList.contains('srsc-overflow-hidden') ? 'fastrr_drawer' : 'theme_drawer',
        cart_count: cartCount()
      });
    }
    drawerOpen = open;
  }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

  if (location.pathname === '/cart') {
    track('momentier_cart_viewed', {
      source: 'cart_page',
      cart_count: number(context.cart?.count),
      value: number(context.cart?.value)
    });
  }

  const CHECKOUT = '#sr-buy-now-button, .sr-buy-now-button, .sr-headless-checkout, [name="sr-headless-button"], button[name="checkout"], a[href="/checkout"], a[href^="/checkout?"], #cart-sidebar-checkout, #cart-checkout';
  let checkoutClickAt = 0;
  function recordCheckout(event) {
    const control = event.composedPath?.().find(node => node?.matches?.(CHECKOUT)) || event.target.closest?.(CHECKOUT);
    if (!control || control.disabled || Date.now() - checkoutClickAt < 1500) return;
    checkoutClickAt = Date.now();
    const attempt = {
      id: nowId(),
      started_at: Date.now(),
      source: control.id || [...control.classList].find(name => /checkout|fastrr|headless/i.test(name)) || 'checkout_control'
    };
    try { sessionStorage.setItem('momentier_checkout_attempt', JSON.stringify(attempt)); } catch (_) {}
    track('momentier_checkout_started', {
      checkout_attempt_id: attempt.id,
      source: attempt.source,
      cart_count: cartCount(),
      value: number(context.cart?.value)
    });
  }
  document.addEventListener('click', recordCheckout, true);

  // Shiprocket Smart Cart renders inside an open shadow root. Bind there as well
  // because events from third-party components are not guaranteed to cross the
  // shadow boundary, while preserving the component's own handler and navigation.
  function bindFastrrCheckout() {
    const shadow = document.querySelector('#shiprocket-smart-cart-root')?.shadowRoot;
    if (!shadow || shadow.__momentierCheckoutBound) return false;
    shadow.__momentierCheckoutBound = true;
    shadow.addEventListener('click', recordCheckout, true);
    const bindControls = () => {
      shadow.querySelectorAll(CHECKOUT).forEach(control => {
        if (control.dataset.momentierCheckoutListener) return;
        control.dataset.momentierCheckoutListener = 'true';
        control.addEventListener('click', recordCheckout, true);
      });
    };
    bindControls();
    new MutationObserver(bindControls).observe(shadow, { childList: true, subtree: true });
    return true;
  }
  if (!bindFastrrCheckout()) {
    const fastrrObserver = new MutationObserver(() => {
      if (bindFastrrCheckout()) fastrrObserver.disconnect();
    });
    fastrrObserver.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => fastrrObserver.disconnect(), 15000);
  }

  try {
    const pending = JSON.parse(sessionStorage.getItem('momentier_checkout_attempt') || 'null');
    if (pending?.started_at && Date.now() - pending.started_at > 15000) {
      track('momentier_checkout_returned', {
        checkout_attempt_id: pending.id,
        seconds_since_checkout: Math.round((Date.now() - pending.started_at) / 1000)
      });
      sessionStorage.removeItem('momentier_checkout_attempt');
    }
  } catch (_) {}

  if ('PerformanceObserver' in window) {
    try { new PerformanceObserver(list => { const entries = list.getEntries(); const last = entries[entries.length - 1]; if (last) state.vitals.lcp = Math.round(last.startTime); }).observe({ type: 'largest-contentful-paint', buffered: true }); } catch (_) {}
    try { new PerformanceObserver(list => { list.getEntries().forEach(entry => { if (!entry.hadRecentInput) state.vitals.cls += entry.value; }); }).observe({ type: 'layout-shift', buffered: true }); } catch (_) {}
    try { new PerformanceObserver(list => { list.getEntries().forEach(entry => { state.vitals.inp = Math.max(state.vitals.inp, Math.round(entry.duration || 0)); }); }).observe({ type: 'event', buffered: true, durationThreshold: 40 }); } catch (_) {}
  }

  let performanceSent = false;
  function reportPerformance() {
    if (performanceSent || !window.performance) return;
    performanceSent = true;
    const navigation = performance.getEntriesByType('navigation')[0];
    const resources = performance.getEntriesByType('resource');
    const fcp = performance.getEntriesByName('first-contentful-paint')[0];
    track('momentier_page_performance', {
      ttfb_ms: navigation ? Math.round(navigation.responseStart) : 0,
      dom_ready_ms: navigation ? Math.round(navigation.domContentLoadedEventEnd) : 0,
      load_ms: navigation ? Math.round(navigation.loadEventEnd || performance.now()) : 0,
      fcp_ms: fcp ? Math.round(fcp.startTime) : 0,
      lcp_ms: state.vitals.lcp,
      cls: Number(state.vitals.cls.toFixed(4)),
      inp_ms: state.vitals.inp,
      request_count: resources.length,
      transfer_kb: Math.round(resources.reduce((sum, entry) => sum + (entry.transferSize || 0), 0) / 1024),
      image_count: document.images.length,
      lazy_image_count: document.querySelectorAll('img[loading="lazy"]').length
    });
  }
  addEventListener('load', () => setTimeout(reportPerformance, 5000), { once: true });
  if (document.readyState === 'complete') setTimeout(reportPerformance, 5000);
  addEventListener('pagehide', reportPerformance, { once: true });
})();
